/**
 * DamageAlteration (Foundry: rule-element/damage-alteration): change the damage type, die
 * faces, dice number or tags of the modifiers and dice of one or more damage selectors.
 */
import type { DamageAlteration, DamageAlterationProperty, DamageAlterationValue } from "../damage-dice";
import type { EngineItem } from "../items";
import { resolveInjectedProperties, resolveValue, cleanRuleValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import { DAMAGE_DICE_FACES, isDamageType } from "../values";
import { AE_LIKE_MODES, type AELikeMode } from "./ae-like";
import type { RuleHandler, RuleInstance } from "./base";

const PROPERTIES = ["damage-type", "dice-faces", "dice-number", "tags"] as const;

interface DamageAlterationData {
  selectors: string[];
  mode: AELikeMode;
  property: DamageAlterationProperty;
  value: unknown;
  relabel: string | null;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const raw = s.selectors ?? s.selector;
  const selectors = (Array.isArray(raw) ? raw : [raw]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("selectors: must have at least 1");
  const mode = s.mode === undefined ? "add" : s.mode;
  if (!(AE_LIKE_MODES as readonly string[]).includes(String(mode))) return rule.fail(`mode: "${String(mode)}" is not a change mode`);
  if (!(PROPERTIES as readonly string[]).includes(String(s.property))) return rule.fail(`property: "${String(s.property)}" is not one of ${PROPERTIES.join(", ")}`);
  const value = s.value === undefined || s.value === null ? null : cleanRuleValue(s.value);
  if (typeof value === "string" && !/^\{\w+\|.+\}$/.test(value) && !/@.+\..+/.test(value)) {
    if (s.property === "damage-type" && !isDamageType(value)) return rule.fail("value: must be a damage type or resolvable to one");
  }
  if (s.property === "dice-faces" && mode === "override" && typeof value === "number" && !(DAMAGE_DICE_FACES as readonly number[]).includes(value)) return rule.fail("value: must override dice faces to 4, 6, 8, 10, or 12");
  rule.data = { selectors, mode: mode as AELikeMode, property: s.property as DamageAlterationProperty, value, relabel: typeof s.relabel === "string" && s.relabel ? s.relabel : null } satisfies DamageAlterationData as unknown as Record<string, unknown>;
}

function resolveAlterationValue(rule: RuleInstance, state: EngineState, data: DamageAlterationData, item: EngineItem | null): DamageAlterationValue {
  const resolvables: Record<string, unknown> = item ? { [item.type === "action" ? "ability" : item.type]: item } : {};
  const resolved = data.value === null ? null : resolveValue(rule, state, data.value, null, { resolvables, warn: false });
  if (rule.ignored) return null;
  const malformed = resolved !== null && typeof resolved !== "number" && typeof resolved !== "string" && !Array.isArray(resolved);
  if (malformed) return null;
  const valid = {
    "damage-type": typeof resolved === "string" && isDamageType(resolved),
    "dice-faces": resolved === null || (typeof resolved === "number" && (DAMAGE_DICE_FACES as readonly number[]).includes(resolved)),
    "dice-number": typeof resolved === "number" && Number.isInteger(resolved) && resolved >= 0 && resolved <= 99,
    tags: (typeof resolved === "string" && resolved === sluggify(resolved)) || (Array.isArray(resolved) && resolved.every((t) => typeof t === "string" && t === sluggify(t))),
  }[data.property];
  if (!valid) {
    const messages = {
      "damage-type": `value: must be a damage type (resolved to ${String(resolved)})`,
      "dice-faces": `value: must be one of 4, 6, 8, 10, and 12 (resolved to ${String(resolved)})`,
      "dice-number": `value: must be a positive integer less than 100 (resolved to ${String(resolved)})`,
      tags: `value: must be a slug or array of slugs (resolved to ${String(resolved)})`,
    };
    rule.fail(messages[data.property]);
    return null;
  }
  return resolved as DamageAlterationValue;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as DamageAlterationData;
  const alteration: DamageAlteration = {
    slug: rule.slug,
    property: data.property,
    mode: data.mode,
    resolve: (item) => resolveAlterationValue(rule, state, data, item),
    predicate: resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false }),
    parentOptions: rule.item.getRollOptions("parent"),
    isIgnored: () => rule.ignored,
    source: rule.item.uuid,
    sourceName: rule.item.name,
    relabel: data.relabel ? (state.catalogue?.localize(data.relabel) ?? data.relabel) : null,
  };
  for (const raw of data.selectors) {
    const selector = resolveInjectedProperties(rule, state, raw);
    if (rule.ignored) return;
    (state.synthetics.damageAlterations[selector] ??= []).push(alteration);
  }
}

export const DamageAlterationHandler: RuleHandler = { key: "DamageAlteration", parse, beforePrepareData };
